import axios from "axios";
import { authService } from "./auth";
import { API_BASE_URL } from "./api";

// Helper function to extract error details from axios error
function handleApiError(error: any): never {
  if (error.response?.data) {
    // Backend returned structured error response
    const { error: message } = error.response.data;
    const statusCode = error.response.status;

    throw new Error(`${message} (${statusCode})`);
  } else if (error.request) {
    // Network error or no response
    throw new Error("Network error - please check your connection");
  } else {
    // Other error
    throw new Error(error.message || "Unknown error occurred");
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      authService.logout();
      window.location.reload(); // Refresh to show login
    }
    return Promise.reject(error);
  },
);

// Draw API interfaces
export interface DrawEligibility {
  isEligible: boolean;
  hasSignedIn: boolean;
  hasPlayedToday: boolean;
  hasSufficientBNB: boolean;
  hasSufficientDXF: boolean;
  alreadyParticipated: boolean;
  thresholds: {
    minBnb: string;
    minDxf: string;
  };
}

export interface DrawCurrentResponse {
  date: string;
  seedHash: string;
  participantCount: number;
  status: 'active' | 'completed' | 'no_participants';
  userParticipation?: {
    seqNumber: number;
    participatedAt: string;
  } | null;
}

export interface DrawParticipateResponse {
  seqNumber: number;
  participatedAt: string;
  participantCount: number;
}

export interface DrawHistoryItem {
  date: string;
  seedHash: string;
  seed?: string;
  participantCount: number;
  winnerSeqNumber?: number | null;
  winnerAddress?: string | null;
  mintTxHash?: string | null;
  status: 'active' | 'completed' | 'no_participants';
}

export interface DrawHistoryResponse {
  draws: DrawHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export class DrawApiService {
  /**
   * Get current day's draw information
   */
  static async getCurrentDraw(): Promise<DrawCurrentResponse> {
    try {
      const response = await api.get('/draw/current');
      return response.data;
    } catch (error) {
      console.error("Error getting current draw:", error);
      handleApiError(error);
    }
  }

  /**
   * Check user's eligibility to participate in today's draw
   * Requires authentication
   */
  static async checkEligibility(): Promise<DrawEligibility> {
    try {
      const response = await api.get('/draw/eligibility');
      return response.data;
    } catch (error) {
      console.error("Error checking draw eligibility:", error);
      handleApiError(error);
    }
  }

  /**
   * Participate in today's draw
   * Requires authentication
   */
  static async participateInDraw(): Promise<DrawParticipateResponse> {
    try {
      const response = await api.post('/draw/participate', {});
      return response.data;
    } catch (error) {
      console.error("Error participating in draw:", error);
      handleApiError(error);
    }
  }

  /**
   * Get draw history with pagination
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 90, max: 90)
   */
  static async getDrawHistory(page: number = 1, limit: number = 90): Promise<DrawHistoryResponse> {
    try {
      const response = await api.get(`/draw/history?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error("Error getting draw history:", error);
      handleApiError(error);
    }
  }
}